import { useRef, useState } from 'react';
import { Modal } from './ui';

// T110 — Themed replacement for window.confirm. Async submit with internal
// `submitting` state; the safe action (Cancel) is focused first so a stray
// Enter can't fire a destructive confirm.
//
// T131 — now built on the shared <Modal> primitive (bare mode: header + footer
// passed as children for a byte-identical look). Modal owns the backdrop, focus
// trap, gated Escape/backdrop close, and the z-index. tone="over" keeps it at
// z-[60] so it always sits above the modal that spawned it; escapeClosesOnTop
// is disabled while submitting (matches the old `!submitting` guards). When this
// is mounted inside another modal, that modal must pass escapeClosesOnTop={false}
// while it's open so one Escape closes only this dialog (the T110/T111 contract).
//
// Props:
//   title       — required, the question (e.g. "Delete this document?")
//   message     — optional supporting line
//   confirmLabel / cancelLabel — button text
//   tone        — 'danger' (red confirm) | 'default' (brand confirm)
//   busyLabel   — confirm-button text while onConfirm is awaiting
//   onConfirm   — async; the dialog disables both buttons until it resolves
//   onCancel    — closes without acting (Escape / backdrop / Cancel)
export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busyLabel,
  onConfirm,
  onCancel,
}) {
  const [submitting, setSubmitting] = useState(false);
  const cancelRef = useRef(null);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm?.();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCls =
    tone === 'danger' ? 'bg-red-700 hover:bg-red-600' : 'bg-brand-600 hover:bg-brand-500';

  return (
    <Modal
      role="dialog"
      size="sm"
      tone="over"
      labelledById="confirm-dialog-title"
      initialFocusRef={cancelRef}
      escapeClosesOnTop={!submitting}
      onClose={submitting ? undefined : onCancel}
    >
      <header className="border-b border-slate-800 p-4">
        <h2 id="confirm-dialog-title" className="text-base font-semibold text-slate-100">
          {title}
        </h2>
        {message && <p className="mt-1 text-xs text-slate-400">{message}</p>}
      </header>
      <footer className="flex items-center justify-end gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
        <button
          ref={cancelRef}
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:opacity-50"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={submitting}
          className={
            'rounded-md px-3 py-1.5 text-xs font-medium text-white transition disabled:opacity-50 ' +
            confirmCls
          }
        >
          {submitting ? (busyLabel ?? '…') : confirmLabel}
        </button>
      </footer>
    </Modal>
  );
}
